'use client'

import { useRef, type ReactNode } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { Zap, Shield, Gauge, Users, Code, Cloud } from 'lucide-react'
import { cn } from '../../../../lib/cn'
import { Markdown } from '../../../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../../../lib/motion'

/**
 * FeaturesDividers - 3x2 grid with gradient divider lines between cells
 *
 * **Layout:**
 * ```
 * ┌─────────────────────────────────────────────┐
 * │            Centered Section Title            │
 * │            Description text                  │
 * ├─────────────┼─────────────┼─────────────────┤
 * │  [icon]     │  [icon]     │   [icon]        │
 * │   Title     │   Title     │    Title        │
 * │   Desc      │   Desc      │    Desc         │
 * ├─────────────┼─────────────┼─────────────────┤
 * │  [icon]     │  [icon]     │   [icon]        │
 * │   Title     │   Title     │    Title        │
 * │   Desc      │   Desc      │    Desc         │
 * └─────────────┴─────────────┴─────────────────┘
 * ```
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Requires exactly 6 features for balanced 3x2 layout on desktop
 * - Each feature needs `icon`, `title`, and `description`
 * - Icons constrained to size-5 (20px) in size-10 (40px) circular background
 * - Best used for comprehensive feature overviews
 */
export interface FeaturesDividersFeature {
  icon: ReactNode
  title: string
  /** Feature description - supports Markdown (bold, code, links) */
  description: string
}

export interface FeaturesDividersProps {
  /** Section heading - centered above grid */
  heading?: string
  /** Section description - centered below heading */
  description?: string
  /** Feature items - requires 6 for balanced 3x2 grid */
  features?: FeaturesDividersFeature[]
  className?: string
}

const defaultFeatures: FeaturesDividersFeature[] = [
  {
    title: 'Easy Integration',
    description: 'Get started in minutes with our simple API and comprehensive documentation.',
    icon: <Zap className="size-6" />,
  },
  {
    title: 'Secure by Default',
    description: 'Enterprise-grade security with automatic updates and vulnerability scanning.',
    icon: <Shield className="size-6" />,
  },
  {
    title: 'High Performance',
    description: 'Built for scale with intelligent caching and edge deployment.',
    icon: <Gauge className="size-6" />,
  },
  {
    title: 'Team Collaboration',
    description: 'Work together seamlessly with real-time editing and version control.',
    icon: <Users className="size-6" />,
  },
  {
    title: 'Developer Experience',
    description: 'Intuitive APIs and comprehensive documentation for rapid development.',
    icon: <Code className="size-6" />,
  },
  {
    title: 'Cloud Native',
    description: 'Deploy anywhere with first-class support for all major cloud providers.',
    icon: <Cloud className="size-6" />,
  },
]

/**
 * FeaturesDividers - Feature grid with gradient divider lines (3x2 layout)
 *
 * @example
 * ```tsx
 * <FeaturesDividers
 *   heading="Everything You Need"
 *   description="All the tools to build amazing products"
 *   features={[
 *     { icon: <Zap />, title: 'Fast', description: 'Speed optimized' },
 *     // ... 5 more features
 *   ]}
 * />
 * ```
 */
const FeaturesDividers = ({
  heading,
  description,
  features = defaultFeatures,
  className,
}: FeaturesDividersProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-24 sm:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        {/* Header section */}
        {(heading || description) && (
          <motion.div className="mx-auto mb-16 max-w-3xl text-center" variants={headerAnimation}>
            {heading && (
              <h2 className="text-pretty text-3xl font-semibold lg:text-4xl">{heading}</h2>
            )}
            {description && (
              <div className="mt-4">
                <Markdown size="lg">{description}</Markdown>
              </div>
            )}
          </motion.div>
        )}

        {/* Grid with divider lines */}
        <div className="relative w-full">
          {/* Horizontal divider line - center (between row 1 and row 2) */}
          <motion.div
            className="pointer-events-none absolute left-0 top-1/2 z-20 hidden h-px w-full -translate-y-1/2 lg:block"
            style={{
              background: 'linear-gradient(to right, var(--background), var(--border), var(--background))',
            }}
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scaleX: 0 }}
            animate={isInView ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0 }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6, delay: 0.8, ease: 'easeOut' }}
          />

          {/* Vertical divider line - 1/3 position */}
          <motion.div
            className="pointer-events-none absolute left-1/3 top-0 z-0 hidden h-full w-px -translate-x-1/2 lg:block"
            style={{
              background: 'linear-gradient(to bottom, var(--background), var(--border), var(--background))',
            }}
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scaleY: 0 }}
            animate={isInView ? { opacity: 1, scaleY: 1 } : { opacity: 0, scaleY: 0 }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6, delay: 0.9, ease: 'easeOut' }}
          />

          {/* Vertical divider line - 2/3 position */}
          <motion.div
            className="pointer-events-none absolute left-2/3 top-0 z-0 hidden h-full w-px -translate-x-1/2 lg:block"
            style={{
              background: 'linear-gradient(to bottom, var(--background), var(--border), var(--background))',
            }}
            initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, scaleY: 0 }}
            animate={isInView ? { opacity: 1, scaleY: 1 } : { opacity: 0, scaleY: 0 }}
            transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6, delay: 1.0, ease: 'easeOut' }}
          />

          {/* Feature grid */}
          <motion.div
            className="relative z-10 grid w-full grid-cols-1 md:grid-cols-2 md:gap-px lg:grid-cols-3"
            variants={gridContainerVariants(0.1, 0.2)}
          >
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                className="relative flex min-h-[180px] flex-col gap-3 p-1 sm:p-9"
                variants={itemAnimation}
              >
                <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:items-center sm:gap-4 sm:text-left">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted-foreground/10">
                    <span className="text-primary [&>svg]:size-5">{feature.icon}</span>
                  </div>
                  <h3 className="text-lg font-semibold">{feature.title}</h3>
                </div>
                <div className="prose prose-sm max-w-none text-center text-muted-foreground sm:text-left prose-p:text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-background prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                  <ReactMarkdown>{feature.description}</ReactMarkdown>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}

export { FeaturesDividers }
